import { ChevronDown, ChevronRight, Users } from 'lucide-react'
import { useState } from 'react'
import { Card, CardHeader } from '@/components/ui/Card'
import { EmptyState } from '@/components/ui/EmptyState'
import { formatEuros } from '@/lib/format'
import { partsSummary } from '@/lib/recurring-distribution'
import type { LiveDistribution, LiveShareLine } from '@/lib/recurring-distribution'

/**
 * Wat het volgende concept uitbetaalt, gerekend op wat er nu op het scherm
 * staat. Bedrag-leidend, met de onderbouwing als uitklapper in plaats van een
 * lopende zin die na een paar regels afkapt.
 */
export function DistributionResult({ result }: { result: LiveDistribution }) {
  return (
    <Card>
      <CardHeader title="Wat elke ontvanger krijgt" subtitle="Berekend uit wat hier nu staat, tot op de cent" />

      {!result.hasDistribution ? (
        <EmptyState
          className="mt-4"
          icon={Users}
          title="Geen vastgelegde verdeling"
          description="Het verdeel-script van de klant bepaalt straks wie wat krijgt."
        />
      ) : !result.complete ? (
        <p className="mt-4 text-sm text-ink-faint">
          Zodra elke regel een verdeling heeft die op 100% sluit, staat hier wat iedereen krijgt.
        </p>
      ) : (
        <div className="mt-4 space-y-3">
          {result.lines.map((line) => (
            <BeneficiaryRow key={line.beneficiary} line={line} />
          ))}
          <div className="flex items-center justify-between rounded-card bg-fill-1 px-4 py-3 text-sm">
            <span className="text-ink-soft">Totaal verdeeld</span>
            <span className="money font-semibold text-ink">{formatEuros(result.subtotalCents / 100)}</span>
          </div>
        </div>
      )}
    </Card>
  )
}

function BeneficiaryRow({ line }: { line: LiveShareLine }) {
  const [open, setOpen] = useState(false)

  return (
    <div className="rounded-card border border-hairline p-4">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <span className="font-medium text-ink">{line.label}</span>
          <p className="mt-1 text-sm text-ink-soft">{partsSummary(line.parts)}</p>
        </div>
        <span className="money shrink-0 font-semibold text-ink">{formatEuros(line.cents / 100)}</span>
      </div>

      {line.parts.length > 1 ? (
        <button
          type="button"
          className="btn-ghost mt-2 h-7 px-1.5 text-xs text-ink-soft"
          onClick={() => setOpen((v) => !v)}
          aria-expanded={open}
          aria-label={`Regels van ${line.label}`}
        >
          {open ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />}
          Regels
        </button>
      ) : null}

      {open ? (
        <ul className="mt-2 divide-y divide-hairline border-t border-hairline">
          {line.parts.map((part, index) => (
            <li key={`${part.description}-${index}`} className="grid grid-cols-[1fr_4rem_6rem] items-center gap-2 py-1.5 text-sm">
              <span className="truncate text-ink-soft">{part.description}</span>
              <span className="num text-right text-ink-faint">
                {part.percentage.toLocaleString('nl-NL', { maximumFractionDigits: 2 })}%
              </span>
              <span className="money text-right text-ink">{formatEuros(part.cents / 100)}</span>
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  )
}
